import { FC, useState } from 'react';
import { Box, Stack } from '@mui/material';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useToast from '@/hooks/use-toast.hook';
import { FieldError } from '@/lib/services';
import {
  createPartnerMember,
  isPartnerUserDomainInvalid,
} from '@/lib/services/partner-user.service';
import { PartnerModel } from '@/lib/models/partner.model';
import { PartnerRoleOptions, PartnerUserModel } from '@/lib/models/partner-user.model';
import { Form } from '@/components/form';
import InputControl from '@/components/form/input-control';
import DrawerButtons from '@/components/form/drawer-buttons';
import { DrawerFormSection } from '../form-section';
import Card from '@/components/card';
import CardHeader from '@/components/card/card-header';
import Button from '@/components/button';
import Chip from '@/components/chip';
import NoneChip from '@/components/chip/none-chip';
import PartnerUserLink from '@/components/partner-user-link';
import InlineToast from '@/components/toasts/inline';

interface Props {
  partner: PartnerModel;
  members: PartnerUserModel[];
  canAdd: boolean;
  onChange: () => void;
}

const formModel = new PartnerUserModel();

const MembersPanel: FC<Props> = ({ partner, members, canAdd, onChange }) => {
  const [adding, setAdding] = useState(false);
  const { toast, errorToast } = useToast();
  const hasDomains = !!partner.domains.length;

  const formHandle = useFormHandle(
    {
      initialValues: formModel,
      validationSchema: formModel.schema,
      validateOnChange: false,
      onSubmit: async (member, { setFieldError, resetForm }) => {
        // The API does not check a partner user's email domain, so the form must.
        if (isPartnerUserDomainInvalid(member, partner)) {
          setFieldError('email', `Email domain must be one of: ${partner.domains.join(', ')}`);
          return;
        }

        const { created, error, fieldErrors } = await createPartnerMember(partner.id, member);

        if (fieldErrors) {
          fieldErrors.forEach(({ msg, path }: FieldError) => setFieldError(path, msg));
          return;
        }

        if (error || !created) {
          errorToast(error || 'The partner admin was not added');
          return;
        }

        toast('The partner admin was added');
        resetForm();
        setAdding(false);
        onChange();
      },
    },
    { lockTarget: 'drawer' }
  );

  const { handleSubmit, canSubmit, isSubmitting, resetForm } = formHandle;

  const handleCancel = () => {
    resetForm();
    setAdding(false);
  };

  if (adding && canAdd) {
    return (
      <Form formHandle={formHandle}>
        <DrawerFormSection label="Add Partner Admin" />
        <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

        <InputControl
          name="email"
          type="email-with-domain"
          options={partner.domainUiOptions}
          formHandle={formHandle}
        />
        <InputControl
          name="role"
          label="Role"
          type="select"
          options={PartnerRoleOptions}
          formHandle={formHandle}
        />

        <DrawerButtons
          submit
          cancel
          isNew
          canSubmit={canSubmit}
          canCancel={!isSubmitting}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Form>
    );
  }

  return (
    <Card>
      <CardHeader title="Partner Admins" icon="people">
        {canAdd && (
          <Button
            label="Add Partner Admin"
            icon="plus"
            size="small"
            disabled={!hasDomains}
            onClick={() => setAdding(true)}
          />
        )}
      </CardHeader>

      {canAdd && !hasDomains && (
        <Box mb={2}>
          <InlineToast
            level="warning"
            message="This partner has no domains, so partner admins cannot be added. Add a domain to the partner first."
          />
        </Box>
      )}

      {!members.length && <NoneChip />}

      <Stack gap={2}>
        {members.map((member) => (
          <Stack key={member.id} direction="row" alignItems="center" gap={1}>
            <Box flexGrow={1} minWidth={0}>
              <PartnerUserLink user={member} partnerId={partner.id} onChange={onChange} />
            </Box>
            {isPartnerUserDomainInvalid(member, partner) && (
              <Chip label="Invalid Domain" color="error" size="small" />
            )}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
};

export default MembersPanel;
